<template>
  <div class="approval-city-config">
    <div class="page-header">
      <div class="page-header__left">
        <h2 class="page-title">Настройки утверждения накладных</h2>
        <span class="page-subtitle">Назначение утверждающих по городу и типу цены</span>
      </div>
      <q-select
        v-model="selectedCity"
        :options="cities"
        option-value="city_id"
        option-label="city_name"
        emit-value
        map-options
        outlined
        dense
        clearable
        label="Выберите город"
        style="min-width: 240px"
        @update:model-value="onCityChange"
      />
    </div>

    <q-linear-progress v-if="loading" indeterminate color="primary" class="q-mt-xs" />

    <template v-if="selectedCity && !loading">
      <div class="config-table-wrap">
        <table class="config-table">
          <thead>
            <tr>
              <th class="col-type">Тип цены</th>
              <th class="col-toggle">Активно</th>
              <th class="col-employees">Утверждающие</th>
              <th class="col-action"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in configList"
              :key="item.approval_type"
              :class="['config-row', { 'config-row--active': item.is_required }]"
            >
              <td class="col-type">
                <div class="type-cell">
                  <q-icon
                    :name="item.approval_type === 'exceeded' ? 'warning' : 'check_circle'"
                    :color="item.approval_type === 'exceeded' ? 'orange-7' : 'primary'"
                    size="18px"
                  />
                  <div>
                    <div class="type-label">{{ item.label }}</div>
                    <div class="type-desc">{{ item.desc }}</div>
                  </div>
                </div>
              </td>
              <td class="col-toggle">
                <q-toggle
                  v-model="item.is_required"
                  :disable="saving"
                  color="primary"
                />
              </td>
              <td class="col-employees">
                <q-select
                  v-model="item.employee_ids"
                  :options="employeeOptions"
                  option-value="employee_id"
                  :option-label="opt => opt.fio || opt.email || ''"
                  emit-value
                  map-options
                  outlined
                  dense
                  multiple
                  use-chips
                  use-input
                  clearable
                  input-debounce="300"
                  placeholder="Начните вводить ФИО..."
                  class="employees-select"
                  :disable="saving || !item.is_required"
                  @filter="onEmployeeFilter"
                >
                  <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section>
                        <q-item-label>{{ scope.opt.fio }}</q-item-label>
                        <q-item-label caption>{{ scope.opt.email }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                  <template v-slot:no-option>
                    <q-item>
                      <q-item-section class="text-grey">Не найдено</q-item-section>
                    </q-item>
                  </template>
                  <template v-slot:selected-item="scope">
                    <q-chip
                      removable
                      dense
                      color="primary"
                      text-color="white"
                      size="sm"
                      :label="getEmployeeName(scope.opt)"
                      @remove="scope.removeAtIndex(scope.index)"
                    />
                  </template>
                </q-select>
              </td>
              <td class="col-action">
                <q-btn
                  unelevated
                  label="Сохранить"
                  color="primary"
                  size="sm"
                  :loading="saving && savingType === item.approval_type"
                  :disable="saving"
                  @click="save(item)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div v-if="!selectedCity && !loading" class="empty-hint">
      <q-icon name="location_city" size="40px" color="grey-4" />
      <div>Выберите город для настройки утверждения накладных</div>
    </div>
  </div>
</template>

<script>
import instance from '@/services/api';
import { supplierRequestsAPI } from '@/views/SupplierRequests/services/api';

export default {
  name: 'InvoiceApprovalConfig',
  data() {
    return {
      cities: [],
      selectedCity: null,
      loading: false,
      saving: false,
      savingType: null,
      employeeOptions: [],
      allEmployees: [],
      configList: [
        {
          approval_type: 'normal',
          label: 'Цена ≤ индикативной',
          desc: 'Введённая цена не превышает индикативную цену заявки',
          is_required: false,
          employee_ids: [],
        },
        {
          approval_type: 'exceeded',
          label: 'Цена > индикативной',
          desc: 'Введённая цена превышает индикативную цену заявки',
          is_required: false,
          employee_ids: [],
        },
      ],
    };
  },
  methods: {
    getEmployeeName(opt) {
      if (!opt) return '';
      if (typeof opt === 'object') return opt.fio || opt.email || '';
      const found = this.allEmployees.find(e => e.employee_id === opt);
      return found ? (found.fio || found.email || '') : String(opt);
    },
    async loadCities() {
      try {
        const res = await instance().get('/common/cities/read_city/');
        this.cities = res?.data?.data || [];
      } catch {
        this.$q.notify({ type: 'negative', message: 'Ошибка загрузки городов' });
      }
    },
    async loadEmployees(search = '') {
      try {
        const res = await supplierRequestsAPI.getInvoiceApprovalEmployees(search);
        this.employeeOptions = res?.data || [];
        if (!search) {
          this.allEmployees = res?.data || [];
        }
      } catch {
        this.employeeOptions = [];
      }
    },
    onEmployeeFilter(val, update) {
      this.loadEmployees(val).then(() => update());
    },
    async onCityChange(cityId) {
      if (!cityId) return;
      await this.loadConfig(cityId);
    },
    async loadConfig(cityId) {
      this.loading = true;
      try {
        const res = await supplierRequestsAPI.getInvoiceApprovalCityConfig(cityId);
        const cfg = res?.config || {};
        this.configList.forEach(item => {
          const c = cfg[item.approval_type];
          item.is_required = c ? c.is_required : false;
          item.employee_ids = c ? (c.employee_ids || []) : [];
        });
        await this.loadEmployees();
        const savedEmployees = res?.saved_employees || [];
        if (savedEmployees.length) {
          const existingIds = new Set(this.allEmployees.map(e => e.employee_id));
          const missing = savedEmployees.filter(e => !existingIds.has(e.employee_id));
          if (missing.length) {
            this.allEmployees = [...this.allEmployees, ...missing];
            this.employeeOptions = [...this.employeeOptions, ...missing];
          }
        }
      } catch {
        this.$q.notify({ type: 'negative', message: 'Ошибка загрузки настроек' });
      } finally {
        this.loading = false;
      }
    },
    async save(item) {
      if (!this.selectedCity) return;
      this.saving = true;
      this.savingType = item.approval_type;
      try {
        await supplierRequestsAPI.updateInvoiceApprovalCityConfig({
          city_id: this.selectedCity,
          approval_type: item.approval_type,
          is_required: item.is_required,
          employee_ids: item.employee_ids,
        });
        this.$q.notify({ type: 'positive', message: 'Сохранено', timeout: 1500 });
      } catch {
        this.$q.notify({ type: 'negative', message: 'Ошибка сохранения' });
      } finally {
        this.saving = false;
        this.savingType = null;
      }
    },
  },
  async mounted() {
    await this.loadCities();
    await this.loadEmployees();
  },
};
</script>

<style scoped lang="scss">
.approval-city-config {
  padding: 20px 24px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.page-header__left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #1a1a2e;
}

.page-subtitle {
  font-size: 12px;
  color: #9e9e9e;
}

.config-table-wrap {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
}

.config-table {
  width: 100%;
  border-collapse: collapse;

  thead tr {
    background: #f5f5f5;
  }

  th {
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #757575;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
    white-space: nowrap;
  }

  td {
    padding: 14px 16px;
    vertical-align: middle;
    border-bottom: 1px solid #f0f0f0;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.config-row--active {
  background: #fafffe;
}

.col-type {
  min-width: 260px;
  width: 280px;
}

.col-toggle {
  width: 80px;
  text-align: center;
}

.col-employees {
  min-width: 380px;
}

.col-action {
  width: 110px;
  text-align: right;
}

.type-cell {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  .q-icon {
    margin-top: 2px;
    flex-shrink: 0;
  }
}

.type-label {
  font-size: 13px;
  font-weight: 600;
  color: #212121;
}

.type-desc {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 2px;
  line-height: 1.4;
}

.employees-select {
  min-width: 320px;
}

.empty-hint {
  margin-top: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #bdbdbd;
  font-size: 14px;
}
</style>
